import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const C = {
  teal: "#2A9D8F", navy: "#1B3A5C", border: "#D6E4F0",
  muted: "#6B85A3", bg: "#F4F8FC", white: "#FFFFFF", lightTeal: "#E8F5F4",
};

const CATEGORIES = [
  { value: "nouvelle-fonctionnalite", label: "💡 Nouvelle fonctionnalité" },
  { value: "bug", label: "🐛 Quelque chose ne fonctionne pas" },
  { value: "amelioration", label: "✨ Améliorer l'existant" },
  { value: "autre", label: "💬 Autre" },
];

export default function FeedbackButton() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [categorie, setCategorie] = useState("");
  const [message, setMessage] = useState("");
  const [wantContact, setWantContact] = useState(false);
  const [email, setEmail] = useState("");
  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user]);

  const reset = () => {
    setCategorie("");
    setMessage("");
    setWantContact(false);
    setSent(false);
  };

  const handleSubmit = async () => {
    if (!user || !message.trim() || !categorie) return;
    setSending(true);
    const { error } = await supabase.from("feedbacks").insert({
      user_id: user.id,
      categorie,
      message: message.trim(),
      email_contact: wantContact ? email : null,
    });
    setSending(false);
    if (error) {
      if (import.meta.env.DEV) console.error("❌ Feedback error:", error.message);
      return;
    }
    setSent(true);
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(reset, 300);
  };

  return (
    <>
      {/* Floating button — redirige vers /auth si non connecté */}
      <button
        onClick={() => user ? setOpen(true) : navigate("/auth?mode=signup")}
        className="fixed z-50 bottom-5 right-5 md:bottom-6 md:right-6 w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center text-xl md:text-2xl shadow-lg transition-all duration-300 hover:scale-110"
        style={{
          backgroundColor: C.teal,
          opacity: 0.85,
          boxShadow: `0 4px 15px rgba(42,157,143,0.3)`,
          transition: "all 0.3s ease",
        }}
        aria-label="Donner un feedback"
      >
        💬
      </button>

      {/* Modal overlay */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={handleClose}
          style={{ backgroundColor: "rgba(27,58,92,0.4)", backdropFilter: "blur(2px)" }}
        >
          <div
            className="w-full rounded-2xl shadow-xl p-6 relative animate-in fade-in zoom-in-95 duration-200"
            style={{ maxWidth: 460, backgroundColor: C.white, border: `1px solid ${C.border}` }}
            onClick={e => e.stopPropagation()}
          >
            {/* Close */}
            <button onClick={handleClose} className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center text-sm hover:bg-gray-100" style={{ color: C.muted }}>✕</button>

            {sent ? (
              /* Confirmation */
              <div className="text-center py-6">
                <div className="text-5xl mb-4">✅</div>
                <div className="text-lg font-bold mb-2" style={{ color: C.navy }}>Merci !</div>
                <div className="text-sm mb-6" style={{ color: C.muted }}>
                  Votre idée a bien été transmise. Nous lisons chaque message avec attention.
                </div>
                <button onClick={handleClose} className="px-6 py-2.5 rounded-xl text-white text-sm font-semibold" style={{ backgroundColor: C.teal }}>
                  Fermer
                </button>
              </div>
            ) : (
              /* Form */
              <>
                <div className="text-base font-bold mb-1" style={{ color: C.navy }}>
                  Une idée pour améliorer PsyGestion ?
                </div>
                <div className="text-xs mb-5" style={{ color: C.muted }}>
                  Votre avis nous aide à construire le meilleur outil pour les psychologues.
                </div>

                {/* Categories */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {CATEGORIES.map(cat => (
                    <button
                      key={cat.value}
                      onClick={() => setCategorie(cat.value)}
                      className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all"
                      style={{
                        borderColor: categorie === cat.value ? C.teal : C.border,
                        backgroundColor: categorie === cat.value ? C.lightTeal : C.white,
                        color: categorie === cat.value ? C.teal : C.navy,
                      }}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>

                {/* Message */}
                <div className="mb-4">
                  <textarea
                    value={message}
                    onChange={e => { if (e.target.value.length <= 500) setMessage(e.target.value); }}
                    placeholder="Ex : J'aimerais pouvoir exporter mes données en Excel..."
                    rows={4}
                    className="w-full rounded-xl border px-4 py-3 text-sm outline-none resize-none"
                    style={{ borderColor: C.border, color: C.navy }}
                  />
                  <div className="text-right text-xs mt-1" style={{ color: message.length > 450 ? "#E63946" : C.muted }}>
                    {message.length}/500
                  </div>
                </div>

                {/* Contact checkbox */}
                <label className="flex items-start gap-2 mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={wantContact}
                    onChange={e => setWantContact(e.target.checked)}
                    className="mt-0.5 rounded"
                    style={{ accentColor: C.teal }}
                  />
                  <span className="text-xs" style={{ color: C.navy }}>
                    Je souhaite être contacté·e si vous avez des questions
                  </span>
                </label>

                {wantContact && (
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="votre@email.com"
                    className="w-full rounded-xl border px-4 py-2 text-sm outline-none mb-4"
                    style={{ borderColor: C.border, color: C.navy }}
                  />
                )}

                {/* Submit */}
                <button
                  onClick={handleSubmit}
                  disabled={!message.trim() || !categorie || sending}
                  className="w-full py-3 rounded-xl text-white font-bold text-sm transition-opacity"
                  style={{
                    backgroundColor: C.teal,
                    opacity: message.trim() && categorie && !sending ? 1 : 0.4,
                    cursor: message.trim() && categorie && !sending ? "pointer" : "not-allowed",
                  }}
                >
                  {sending ? "Envoi..." : "Envoyer mon idée →"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
